<template>
  <div class="px-[24px] py-[32px] font-[Inter]">
    <h4 class="text-text-display-lg font-black tracking-[-0.03em] mb-[8px] text-brown-800">
      A quick check of your deets ✨
    </h4>
    <div class="font-semibold mb-[24px] text-brown-800">
      We're excited too, but let's make sure your details are spot on!
    </div>

    <div class="w-full flex flex-col gap-4">
      <div>
        <label :class="labelClass" for="givenName">Full Name</label>
        <DsInput
          id="givenName"
          name="givenName"
          v-model="data.givenName"
          placeholder="Enter your Full Name"
          :error="shownError('givenName')"
          @blur="touch('givenName')"
        />
      </div>

      <div>
        <label :class="labelClass" for="familyName">Family Name</label>
        <DsInput
          id="familyName"
          name="familyName"
          v-model="data.familyName"
          placeholder="Enter your Family Name"
          :error="shownError('familyName')"
          @blur="touch('familyName')"
        />
      </div>

      <div>
        <label :class="labelClass" for="preferredName">Preferred Name</label>
        <DsInput
          id="preferredName"
          name="preferredName"
          v-model="data.preferredName"
          placeholder="Enter your Preferred Name"
          :error="shownError('preferredName')"
          @blur="touch('preferredName')"
        />
      </div>

      <div class="mb-6">
        <span :class="labelClass">Gender</span>
        <div class="flex gap-2">
          <DsRadioCard
            v-for="option in genderOptions"
            :key="option.value"
            class="flex-1"
            name="gender"
            align="center"
            hide-indicator
            :label="option.label"
            :value="option.value"
            :model-value="data.gender"
            :disabled="isGenderDisabled"
            @update:modelValue="onSelectGender"
          />
        </div>
      </div>

      <div>
        <label :class="labelClass" for="nationality">Nationality</label>
        <DsSearchableDropdown
          id="nationality"
          name="nationality"
          v-model="data.nationality"
          :options="nationalityOptions"
          placeholder="Enter nationality"
          search-placeholder="Search nationality"
          :error="shownError('nationality')"
          @change="touch('nationality')"
        />
      </div>

      <div v-if="!pidOnRecord" class="flex flex-col gap-4">
        <div>
          <label :class="labelClass" for="dateOfBirth">Date of Birth</label>
          <DsDatePicker
            id="dateOfBirth"
            name="dateOfBirth"
            v-model="data.dateOfBirth"
            display-format="DD-MM-YYYY"
            placeholder="Enter your Date of Birth"
            :max="today"
            :error="shownError('dateOfBirth')"
            @change="touch('dateOfBirth')"
          />
        </div>
        <div>
          <label :class="labelClass" for="partialNric">
            Last 4 chars of NRIC/FIN/Passport no.
          </label>
          <DsInput
            id="partialNric"
            name="partialNric"
            v-model="data.partialNric"
            placeholder="Eg: 123A"
            :max-length="4"
            :error="shownError('partialNric')"
            @blur="touch('partialNric')"
          />
        </div>
        <DsLink size="sm" underline="always" class="self-start" @click="onInfo">
          What is this information used for?
        </DsLink>
      </div>
    </div>

    <div v-if="register.personalId" class="mt-[24px]">
      <BioRequestCard :payload="register" />
    </div>
    <!-- Giving per login: the member's personal id is set, but this login has not
         confirmed it, so the API hides it. Nothing to fill in here. -->
    <p
      v-else-if="pidOnRecord"
      class="mt-[24px] text-sm font-inter font-medium text-secondary"
      data-test="register-personal-id-locked"
    >
      Your date of birth and NRIC are already on record. Verify them in MyGiving to see them on this
      account.
    </p>

    <div
      v-if="supportNotice"
      class="mt-[24px] flex flex-col gap-3"
      data-test="register-support-notice"
    >
      <DsAlert variant="error">{{ supportNotice }}</DsAlert>
      <ContactSupportButton />
    </div>

    <div :style="hasFocusedField ? `padding-bottom: ${keyboardInsetCss}` : ''">
      <DsButton
        variant="secondary"
        shape="pill"
        size="56"
        class="mt-[64px] w-full !text-text-body-md"
        :disabled="isButtonDisabled"
        :loading="isSaving"
        @click="handleClick"
      >
        {{ isSaving ? 'Saving...' : 'Save and Next' }}
      </DsButton>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapState } from 'vuex';
import {
  Alert as DsAlert,
  Button as DsButton,
  DatePicker as DsDatePicker,
  Input as DsInput,
  Link as DsLink,
  RadioCard as DsRadioCard,
  SearchableDropdown as DsSearchableDropdown,
} from '@team-hogc/hogc-one-design-system';
import BioRequestCard from '@/pages/profile/BioRequestCard.vue';
import ContactSupportButton from '@/components/common/ContactSupportButton.vue';
import { isPersonalIdClash, personalIdClashMessage } from '@/utils/giving-login';
import { ERROR_REQUIRED } from '@/constants/validation/error-messages';
import {
  validateDateOfBirth,
  validateFamilyName,
  validateName,
  validatePartialNric,
} from '@/utils/registration-validation';
import keyboardFieldIntoView from '@/mixins/keyboard-field-into-view';

// Which `register` key each form field is stored under. `nationality` is the
// one that is not a straight rename.
const STORE_KEYS = {
  givenName: 'givenName',
  familyName: 'familyName',
  preferredName: 'preferredName',
  gender: 'gender',
  nationality: 'nationalityId',
  dateOfBirth: 'dateOfBirth',
  partialNric: 'partialNric',
};

export default {
  mixins: [keyboardFieldIntoView],
  components: {
    BioRequestCard,
    ContactSupportButton,
    DsAlert,
    DsButton,
    DsDatePicker,
    DsInput,
    DsLink,
    DsRadioCard,
    DsSearchableDropdown,
  },
  data() {
    return {
      data: {
        givenName: '',
        familyName: '',
        preferredName: '',
        gender: '',
        nationality: '',
        dateOfBirth: '',
        partialNric: '',
      },
      touched: {},
      isLoading: false,
      isSaving: false,
      partialEverPrefilled: false,
      // Set by a 409 PERSONAL_ID_CLASH: shown with a Contact support button.
      supportNotice: '',
      labelClass: 'block text-base text-primary font-inter font-medium mb-1 leading-5',
      genderOptions: [
        { value: 'M', label: 'Male 🙋🏻‍♂' },
        { value: 'F', label: 'Female 🙋🏻‍♀️' },
      ],
    };
  },
  computed: {
    ...mapState('profile', {
      formloading: state => state.loading,
      register: state => state.register,
      errorMessages: state => state.errorMessages,
    }),
    ...mapState('user', ['token']),
    ...mapGetters('registration', ['nationalityForSelect']),

    // The member has a personal id: shown (BioRequestCard), or held back from this
    // login until it confirms it (personalIdLocked). Either way not typed here.
    pidOnRecord() {
      return Boolean(this.register.personalId) || this.register.personalIdLocked === true;
    },

    // The DS dropdown matches by identity and nationality ids arrive as
    // numbers, so both sides work in strings and the payload converts back.
    nationalityOptions() {
      return this.nationalityForSelect.map(({ value, label }) => ({
        value: String(value),
        label,
      }));
    },
    today() {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${now.getFullYear()}-${month}-${day}`;
    },
    errors() {
      const d = this.data;
      const errors = {};
      const set = (field, message) => {
        if (message) errors[field] = message;
      };

      set('givenName', this.isEmpty(d.givenName) ? ERROR_REQUIRED : validateName(d.givenName));
      set(
        'familyName',
        this.isEmpty(d.familyName)
          ? ERROR_REQUIRED
          : validateName(d.familyName) || validateFamilyName(d.familyName, d.givenName),
      );
      set('preferredName', validateName(d.preferredName));
      if (this.isEmpty(d.gender)) set('gender', ERROR_REQUIRED);
      if (this.isEmpty(d.nationality)) set('nationality', ERROR_REQUIRED);

      if (!this.pidOnRecord) {
        set('dateOfBirth', validateDateOfBirth(d.dateOfBirth));
        set('partialNric', validatePartialNric(d.partialNric));
      }

      return errors;
    },
    isButtonDisabled() {
      if (this.isSaving) return true;
      if (Object.keys(this.errors).length > 0) return true;

      // A member whose partial NRIC arrived prefilled may not clear it.
      if (!this.pidOnRecord) {
        return this.partialEverPrefilled && this.isEmpty(this.data.partialNric);
      }
      return false;
    },
    isGenderDisabled() {
      // Disable gender field if user already has a gender value
      return !!(this.register?.gender && this.register.gender.trim() !== '');
    },
  },

  watch: {
    // The profile arrives after this screen mounts, and can keep arriving
    // (stage-1 save, a refetch), so the form takes whatever the store has that
    // it does not. Only non-empty values overwrite, so a field the member has
    // deliberately cleared is not refilled under them.
    register: {
      deep: true,
      immediate: true,
      handler() {
        this.applyRegisterToForm();
      },
    },
    data: {
      deep: true,
      handler() {
        this.pushFormToStore();
      },
    },
  },

  async mounted() {
    this.startKeyboardOverlapTracking();

    this.isLoading = true;
    try {
      await this.$store.dispatch('profile/fetchProfile');
      await this.$store.dispatch('registration/fetchNationality');
      this.applyRegisterToForm();
      this.partialEverPrefilled = !this.isEmpty(this.data.partialNric);
    } catch (e) {
      console.error(e);
    } finally {
      this.isLoading = false;
    }
  },

  methods: {
    inputRegisterForm(key, value) {
      this.$store.dispatch('profile/inputRegisterForm', { key, value });
    },

    // What the profile stores for a field the member has filled in here.
    // An empty preferred name is `null`, not '': the welcome card at the end of
    // onboarding falls back to the given name on null alone, so an empty string
    // would greet the member with nothing at all.
    toStoreValue(field, value) {
      if (field === 'nationality') return this.isEmpty(value) ? '' : Number(value);
      if (field === 'preferredName') return this.isEmpty(value) ? null : value;
      return value;
    },

    applyRegisterToForm() {
      const register = this.register ?? {};
      for (const [field, storeKey] of Object.entries(STORE_KEYS)) {
        const stored = register[storeKey];
        if (this.isEmpty(stored)) continue;
        const next = field === 'nationality' ? String(stored) : stored;
        if (this.data[field] !== next) this.data[field] = next;
      }
    },

    pushFormToStore() {
      for (const [field, storeKey] of Object.entries(STORE_KEYS)) {
        // Gender is read-only once the member already has one on record.
        if (field === 'gender' && this.isGenderDisabled) continue;
        // Date of birth and partial NRIC are only on the form while the member
        // has no personalId; with one, the request card owns them.
        if (this.pidOnRecord && (field === 'dateOfBirth' || field === 'partialNric')) {
          continue;
        }
        const value = this.data[field];
        const normalised = this.toStoreValue(field, value);
        if (this.register[storeKey] !== normalised) {
          this.inputRegisterForm(storeKey, normalised);
        }
      }
    },

    touch(field) {
      this.touched = { ...this.touched, [field]: true };
    },

    shownError(field) {
      return this.touched[field] ? this.errors[field] || '' : '';
    },

    onSelectGender(value) {
      if (this.isGenderDisabled) return;
      this.data.gender = value;
      this.touch('gender');
    },

    async handleClick() {
      if (this.isSaving) return;

      this.touched = Object.fromEntries(Object.keys(this.data).map(field => [field, true]));
      if (this.isButtonDisabled) return;

      // Explicitly construct payload with only source fields needed by API
      const payload = {
        familyName: this.data.familyName,
        givenName: this.data.givenName,
        preferredName: this.data.preferredName || null,
        gender: this.data.gender,
        nationalityId: Number(this.data.nationality),
        dateOfBirth: this.pidOnRecord ? this.register.dateOfBirth : this.data.dateOfBirth,
      };

      // Add partialNric only if it has a valid value (API validation requires it to be omitted or have valid value)
      const partialNricValue = this.pidOnRecord ? this.register.partialNric : this.data.partialNric;
      if (partialNricValue && partialNricValue !== '' && partialNricValue !== null) {
        payload.partialNric = partialNricValue;
      }

      this.isSaving = true;
      this.supportNotice = '';

      try {
        await axios.put(process.env.VUE_APP_API_URL + `/v2/profile/stage-1`, payload, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });
        this.inputRegisterForm('stage', 1);
        // Save onboarding status to localStorage after successful stage-1 completion
        this.$store.dispatch('profile/updateOnboardingStatusAfterStage', 1);
      } catch (e) {
        // The status only: the request carried the member's NRIC and birthday.
        console.error('Stage 1 save error:', e?.response?.status ?? e?.message);
        if (isPersonalIdClash(e)) {
          // Stage 1 fails as a whole on a clash; staff sort it out, so offer support.
          this.supportNotice = personalIdClashMessage(e);
        } else {
          this.$store.dispatch('toast/error', 'Something went wrong saving your details');
        }
      } finally {
        this.isSaving = false;
      }
    },
    onInfo() {
      this.$emit('onInfo');
    },
    isEmpty(v) {
      return v == null || (typeof v === 'string' && v.trim() === '');
    },
  },
};
</script>
